// Dashboard.jsx
import { useState, useEffect, useRef, useMemo } from "react"
import toast from "react-hot-toast"
import {
  X, ChevronDown, Check,
  UploadCloud, User, Menu,
} from "lucide-react"
import AddResumeModal from "../components/AddResumeModal"
import { useUser } from "../contexts/UserContext"
import { useAuth } from "../contexts/AuthContext"
import { useNavigate, useLocation } from "react-router-dom"
import DesktopJobCard, { DesktopJobCardSkeleton } from "../components/DesktopJobCard"
import DashboardRightPanel from "../components/dashboard/DashboardRightPanel"
import AgentApplyPanel from "../components/dashboard/AgentApplyPanel"
import { doc, setDoc, deleteDoc, getDoc, collection, onSnapshot, getDocs, query, where, orderBy, limit, Timestamp } from "firebase/firestore"
import { db, ensureRemoteConfig, rcSwipeCountADay } from "../firebaseConfig"
import {
  LOCATIONS,
} from "../components/dashboard/Constants"
import MobileDrawer from "../components/dashboard/Mobiledrawer"
import MobileFilterPanel from "../components/dashboard/MobileFilterPanel"
import CompanyPickerModal from "../components/dashboard/CompanyPickerModal"
import LocationFilter, { ANYWHERE_VALUE } from "../components/dashboard/LocationFilter"
import FilterBar from "../components/dashboard/FilterBar"
import RoleSelectionModal from "../components/dashboard/RoleSelectionModal"
import ApplicationsTab, { APPLY_AGENT_API, DEFAULT_WORKDAY_PASSWORD } from "../components/dashboard/ApplicationsTab"
import Sidebar from "../components/Sidebar"
import ApplyAnimation from "../components/ApplyAnimation"
import JobDescPanel from "../components/dashboard/JobDescPanel"
import WildTab from "../components/dashboard/WildTab"
import InboxTab from "../components/dashboard/InboxTab"
import ProfileTab from "../components/dashboard/ProfileTab"
import MobileHomeTab from "../components/dashboard/MobileHomeTab"
import MobileProfileTab from "../components/dashboard/MobileProfileTab"
import MobileApplicationsTab from "../components/dashboard/MobileApplicationsTab"
import MobileInboxTab from "../components/dashboard/MobileInboxTab"
import MobileWildTab from "../components/dashboard/MobileWildTab"
import taxonomyData from "../assets/taxonomy.json"

// Flat lookup: taxonomyId → { taxonomyId, title }
const TAXONOMY_MAP = new Map()
for (const cat of taxonomyData.result || []) {
  for (const sub of cat.second_category || []) {
    for (const item of sub.third_category || []) {
      TAXONOMY_MAP.set(item.id, { taxonomyId: item.id, title: item.display_name })
    }
  }
}

import logoImg from "../assets/logo.png"
import logoImgDark from "../assets/logo_dark_theme.png"

// All third-category items under Software/Internet/AI (id: 01-00-00) — used as
// the default taxonomy payload for the Entry Level tab when no role is selected.
const SWE_DEFAULT_TAXONOMY = (() => {
  const cat = taxonomyData.result.find(c => c.id === "01-00-00")
  if (!cat) return []
  return cat.second_category.flatMap(sub =>
    sub.third_category.map(t => ({ taxonomyId: t.id, title: t.display_name }))
  )
})()

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const API_ENDPOINT = "https://jobscanner-e1dd4.ue.r.appspot.com/network/search/mapp"
const APPLICATIONS_API = "https://jobscanner-e1dd4.ue.r.appspot.com/applications"
const DASHBOARD_TAB_ROUTES = {
  console: "/dashboard/home",
  "entry-level": "/dashboard/entry-level",
  applications: "/dashboard/applications",
  wild: "/dashboard/wild",
  inbox: "/dashboard/inbox",
  profile: "/dashboard/profile",
}

function dashboardTabFromPath(pathname) {
  if (pathname.startsWith("/dashboard/applications")) return "applications"
  if (pathname.startsWith("/dashboard/entry-level")) return "entry-level"
  if (pathname.startsWith("/dashboard/wild")) return "wild"
  if (pathname.startsWith("/dashboard/inbox")) return "inbox"
  if (pathname.startsWith("/dashboard/profile")) return "profile"
  return "console"
}

const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"

// ── Theme objects ─────────────────────────────────────────────
const CDark = {
  bg: "#0f0f10",
  bgPage: "#0a0a0b",
  bgSidebar: "#0f0f10",
  bgCard: "#17171a",
  bgCardHov: "#1d1d20",
  bgChip: "#1d1d20",
  bgMuted: "#141416",
  border: "1px solid #27272a",
  borderColor: "#27272a",
  borderMid: "#3f3f46",
  text: "#f4f4f5",
  textMuted: "#d4d4d8",
  textDim: "#a1a1aa",
  textTertiary: "#71717a",
  accent: "#f4f4f5",
  shadow: "0 1px 4px rgba(0,0,0,0.5)",
  sbBorder: "#27272a",
  sbHov: "#1d1d20",
  sbActive: "#2a2a2e",
  sbActiveBg: "#2a2a2e",
  sbActiveBorder: "rgba(255,255,255,0.08)",
  sbActiveText: "#f4f4f5",
  sbText: "#a1a1aa",
  sbMuted: "#52525b",
  sbDivider: "#27272a",
  topbarBg: "#0f0f10",
  mainBg: "#0a0a0b",
  filterBtnBg: "#17171a",
  filterBtnBorder: "#2d2d32",
  filterBtnText: "#a1a1aa",
  popupBg: "#1d1d20",
  popupBorder: "#2d2d32",
  popupDivider: "#27272a",
  chipBg: "#1d1d20",
  chipText: "#d4d4d8",
  inputBg: "#17171a",
  inputBorder: "#2d2d32",
  inputFocusBorder: "#3f3f46",
  isDark: true,
  accentText: "#f4f4f5",
  accentBg: "rgba(255,255,255,0.07)",
  accentBorder: "rgba(255,255,255,0.18)",
  accentBadgeBg: "rgba(255,255,255,0.1)",
  checkFillBg: "#f4f4f5",
  checkFillIcon: "#0a0a0b",
  // JobCard legacy
  cardText: "#f4f4f5",
  cardCompany: "#a1a1aa",
  cardSalary: "#a1a1aa",
  cardMetaBorder: "rgba(255,255,255,0.1)",
  cardMetaBg: "rgba(255,255,255,0.05)",
  cardMetaText: "#f0ede9",
  cardBookmark: "#f0ede9",
  // RoleTaxonomyFilter chips
  taxonomyChipBorder: "rgba(255,255,255,0.09)",
  taxonomyChipBg: "rgba(255,255,255,0.03)",
  taxonomyChipHovBg: "rgba(255,255,255,0.07)",
  taxonomyChipHovBorder: "rgba(255,255,255,0.18)",
  // Misc
  skBg: "#1e1c19",
  checkActiveBg: "#141e19",
  selectedItemBg: "#1a2e25",
  sidebarBg: "#0f0e0d",
  profileDropdownHov: "#2a1a1a",
}

const CLight = {
  bg: "#F7F7F8",
  bgPage: "#F7F7F8",
  bgSidebar: "#FAFAFA",
  bgCard: "#FFFFFF",
  bgCardHov: "#FAFAFA",
  bgChip: "#EBEBED",
  bgMuted: "#EEEEEF",
  border: "1px solid #E4E4E7",
  borderColor: "#E4E4E7",
  borderMid: "#D4D4D8",
  text: "#18181b",
  textMuted: "#3f3f46",
  textDim: "#71717a",
  textTertiary: "#a1a1aa",
  accent: "#18181b",
  shadow: "0 1px 2px rgba(0,0,0,0.04)",
  sbBorder: "#EEEEEE",
  sbHov: "#F4F4F5",
  sbActive: "#18181b",
  sbActiveBg: "#18181b",
  sbActiveBorder: "transparent",
  sbActiveText: "#ffffff",
  sbText: "#52525b",
  sbMuted: "#a1a1aa",
  sbDivider: "#EEEEEE",
  topbarBg: "#F7F7F8",
  mainBg: "#F7F7F8",
  filterBtnBg: "#FFFFFF",
  filterBtnBorder: "#E4E4E7",
  filterBtnText: "#52525b",
  popupBg: "#FFFFFF",
  popupBorder: "#E4E4E7",
  popupDivider: "#F4F4F5",
  chipBg: "#F4F4F5",
  chipText: "#3f3f46",
  inputBg: "#FFFFFF",
  inputBorder: "#E4E4E7",
  inputFocusBorder: "#A1A1AA",
  isDark: false,
  accentText: "#18181b",
  accentBg: "#F4F4F5",
  accentBorder: "#D4D4D8",
  accentBadgeBg: "#E4E4E7",
  checkFillBg: "#18181b",
  checkFillIcon: "#ffffff",
  // JobCard legacy
  cardText: "#18181b",
  cardCompany: "#71717a",
  cardSalary: "#52525b",
  cardMetaBorder: "#E4E4E7",
  cardMetaBg: "#F4F4F5",
  cardMetaText: "#3f3f46",
  cardBookmark: "#52525b",
  // RoleTaxonomyFilter chips
  taxonomyChipBorder: "#E4E4E7",
  taxonomyChipBg: "#F8F8F8",
  taxonomyChipHovBg: "#F0F0F0",
  taxonomyChipHovBorder: "#D4D4D8",
  // Misc
  skBg: "#E5E5E5",
  checkActiveBg: "rgba(0,0,0,0.05)",
  selectedItemBg: "rgba(0,0,0,0.06)",
  sidebarBg: "#FAFAFA",
  profileDropdownHov: "#F5F5F5",
}

function normaliseLocation(loc = "") {
  return loc.trim().toLowerCase().replace(/\s+/g, "-")
}
function parseLocations(locationStr = "") {
  return locationStr.split(",").map(l => normaliseLocation(l)).filter(Boolean)
}
function parseSeniority(seniorityStr = "") {
  return seniorityStr.split(",").map(s => s.trim()).filter(Boolean)
}

const FRESHERS_DEFAULT_LOCATION = ANYWHERE_VALUE
const FRESHERS_DEFAULT_LEVELS   = []

// ── Spinner ───────────────────────────────────────────────────
function Spinner({ C }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 14 }}>
      <div style={{ width: 28, height: 28, border: `2px solid ${C.borderColor}`, borderTopColor: C.accent, borderRadius: "50%", animation: "spin 0.75s linear infinite" }} />
      <span style={{ fontSize: 12, fontWeight: 500, fontFamily: FF, color: C.textDim, letterSpacing: "0.04em" }}>Loading jobs…</span>
    </div>
  )
}

// ── ErrorState ────────────────────────────────────────────────
function ErrorState({ onRetry, C }) {
  const ghostBg  = C.isDark ? "#1e1c19" : "#EEEEEE"
  const ghostBrd = C.isDark ? "rgba(255,255,255,0.05)" : "#E4E4E4"
  const ghostEl1 = C.isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"
  const ghostEl2 = C.isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.08)"
  const ghostTag = C.isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"
  const cards = [
    { title: "72%", company: "48%", tags: [58, 44, 68] },
    { title: "60%", company: "64%", tags: [50, 66, 42] },
    { title: "68%", company: "52%", tags: [62, 38, 54] },
  ]
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "55vh", padding: "40px 24px", textAlign: "center" }}>

      {/* Ghost cards + gradient fade */}
      <div style={{ position: "relative", width: "100%", maxWidth: 320, marginBottom: 32 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, pointerEvents: "none", userSelect: "none" }}>
          {cards.map((g, i) => (
            <div key={i} style={{ background: ghostBg, borderRadius: 14, border: `1px solid ${ghostBrd}`, padding: "16px", opacity: 0.55 - i * 0.16 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: ghostEl1, flexShrink: 0 }} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7, paddingTop: 2 }}>
                  <div style={{ height: 7, background: ghostEl1, borderRadius: 4, width: g.company }} />
                  <div style={{ height: 12, background: ghostEl2, borderRadius: 4, width: g.title }} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {g.tags.map((w, j) => (
                  <div key={j} style={{ height: 22, width: w, background: ghostTag, borderRadius: 6, border: `1px solid ${ghostBrd}` }} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "65%", background: `linear-gradient(to bottom, transparent, ${C.mainBg})`, pointerEvents: "none" }} />
      </div>

      <div style={{ marginBottom: 14, opacity: 0.4 }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.textDim} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
      </div>

      <p style={{ margin: "0 0 7px", fontSize: 17, fontWeight: 700, color: C.text, fontFamily: FF, letterSpacing: "-0.02em" }}>
        Couldn't load jobs
      </p>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: C.textDim, fontFamily: FF, lineHeight: 1.65, maxWidth: 220 }}>
        Check your connection and try again.
      </p>

      <button
        onClick={onRetry}
        style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 40, padding: "0 20px", background: C.accent, border: "none", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: FF, color: "#0a0a0a", transition: "opacity 0.12s" }}
        onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
        onMouseLeave={e => e.currentTarget.style.opacity = "1"}
      >
        Try again
      </button>
    </div>
  )
}

// ── Pagination ────────────────────────────────────────────────
function Pagination({ current, total, hasMore, onChange, C }) {
  const btnBase = {
    width: 34, height: 34, borderRadius: "50%",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    border: "none", fontFamily: FF, outline: "none", flexShrink: 0,
    transition: "background 0.15s, color 0.15s", cursor: "pointer",
  }
  const numBtn = (active) => ({
    ...btnBase,
    background: active ? C.checkFillBg : C.bgChip,
    color: active ? C.checkFillIcon : C.textDim,
    fontSize: 13, fontWeight: active ? 700 : 500,
    cursor: active ? "default" : "pointer",
  })
  const arrowBtn = () => ({
    ...btnBase,
    background: C.bgChip, color: C.text,
    opacity: 1, cursor: "pointer",
  })

  const lastPage = total > 0 ? total : null
  const isFirst  = current === 1
  const isLast   = lastPage ? current >= lastPage : !hasMore

  const getPageNumbers = () => {
    if (!lastPage) {
      const ps = []
      if (current > 1) ps.push(current - 1)
      ps.push(current)
      if (hasMore) ps.push(current + 1)
      return ps
    }
    if (lastPage <= 7) return Array.from({ length: lastPage }, (_, i) => i + 1)

    const set = new Set([1, 2, lastPage - 1, lastPage])
    for (let i = Math.max(1, current - 1); i <= Math.min(lastPage, current + 1); i++) set.add(i)
    const sorted = [...set].sort((a, b) => a - b)
    const result = []
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push("...")
      result.push(sorted[i])
    }
    return result
  }

  const pageNums = getPageNumbers()

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "28px 0 44px", flexWrap: "wrap" }}>
      <button onClick={() => !isFirst && onChange(current - 1)} style={arrowBtn()}>
        <ChevronDown size={16} strokeWidth={3} style={{ transform: "rotate(90deg)" }} />
      </button>
      {pageNums.map((p, i) =>
        p === "..." ? (
          <span key={`dot-${i}`} style={{ width: 34, textAlign: "center", fontSize: 13, color: C.textTertiary, fontFamily: FF, letterSpacing: "0.05em", userSelect: "none" }}>
            ···
          </span>
        ) : (
          <button key={p} onClick={() => p !== current && onChange(p)} style={numBtn(p === current)}>
            {p}
          </button>
        )
      )}
      <button onClick={() => !isLast && onChange(current + 1)} style={arrowBtn()}>
        <ChevronDown size={16} strokeWidth={3} style={{ transform: "rotate(-90deg)" }} />
      </button>
    </div>
  )
}

// ── MultiFilter ───────────────────────────────────────────────
function MultiFilter({ label, options, selected, onToggle, onClear, single, C }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const count = selected.length
  const isActive = count > 0

  useEffect(() => {
    if (!open) return
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [open])

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          height: 28, padding: "0 10px",
          background: isActive ? C.accentBg : C.filterBtnBg,
          border: `1px solid ${isActive ? C.accentBorder : C.filterBtnBorder}`,
          borderRadius: 7, fontSize: 12, fontWeight: isActive ? 600 : 400,
          color: isActive ? C.accentText : C.filterBtnText,
          fontFamily: FF, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
        }}
      >
        {label}
        {isActive && !single && (
          <span style={{ background: C.accentBadgeBg, borderRadius: 9999, padding: "0px 6px", fontSize: 10, fontWeight: 700, lineHeight: "17px", color: C.accentText }}>{count}</span>
        )}
        <ChevronDown size={10} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", opacity: 0.6 }} />
      </button>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, minWidth: 220, background: C.popupBg, border: `1px solid ${C.popupBorder}`, borderRadius: 12, boxShadow: C.isDark ? "0 4px 6px -1px rgba(0,0,0,0.5), 0 16px 40px rgba(0,0,0,0.6)" : "0 4px 6px -1px rgba(0,0,0,0.08), 0 16px 40px rgba(0,0,0,0.12)", zIndex: 200, overflow: "hidden", animation: "fadeUp 0.15s ease" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px 8px", borderBottom: `1px solid ${C.popupDivider}` }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: C.textTertiary, fontFamily: FF, textTransform: "uppercase" }}>{label}</span>
            {count > 0 && (
              <button onClick={(e) => { e.stopPropagation(); onClear() }} style={{ fontSize: 11, fontWeight: 600, color: "#dc2626", background: "none", border: "none", cursor: "pointer", fontFamily: FF, display: "flex", alignItems: "center", gap: 3 }}>
                <X size={10} />Clear
              </button>
            )}
          </div>
          <div style={{ maxHeight: 280, overflowY: "auto", padding: "6px 8px" }}>
            {options.map(opt => {
              const isChecked = selected.includes(opt.value)
              return (
                <button key={opt.value} onClick={() => { onToggle(opt.value); if (single) setOpen(false) }}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", background: isChecked ? C.checkActiveBg : "none", border: "none", borderRadius: 8, cursor: "pointer", textAlign: "left", transition: "background 0.1s", fontFamily: FF }}
                  onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = C.bgMuted }}
                  onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = "none" }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, border: isChecked ? "none" : `1.5px solid ${C.borderMid}`, background: isChecked ? C.checkFillBg : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.12s" }}>
                    {isChecked && <Check size={9} color={C.checkFillIcon} strokeWidth={2.5} />}
                  </div>
                  <span style={{ fontSize: 13, color: isChecked ? C.accentText : C.textMuted, fontWeight: isChecked ? 600 : 400, fontFamily: FF }}>{opt.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Company filter button ─────────────────────────────────────
function CompanyFilterButton({ selected, onClick, C }) {
  const isActive = selected.length > 0
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        height: 28, padding: "0 10px",
        background: isActive ? C.accentBg : C.filterBtnBg,
        border: `1px solid ${isActive ? C.accentBorder : C.filterBtnBorder}`,
        borderRadius: 7, fontSize: 12, fontWeight: isActive ? 600 : 400,
        color: isActive ? C.accentText : C.filterBtnText,
        fontFamily: FF, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
      }}
    >
      Company
      {isActive && (
        <span style={{ background: C.accentBadgeBg, borderRadius: 9999, padding: "0px 6px", fontSize: 10, fontWeight: 700, lineHeight: "17px", color: C.accentText }}>
          {selected.length}
        </span>
      )}
      <ChevronDown size={10} style={{ opacity: 0.6 }} />
    </button>
  )
}

function ResumePickerButton({ resumes, selectedResumeId, onSelect, C }) {
  const [open, setOpen] = useState(false)
  const selected = resumes.find(r => r.id === selectedResumeId)
  const isActive = !!selectedResumeId

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          height: 28, padding: "0 10px",
          background: isActive ? C.accentBg : C.filterBtnBg,
          border: `1px solid ${isActive ? C.accentBorder : C.filterBtnBorder}`,
          borderRadius: 7, fontSize: 12, fontWeight: isActive ? 600 : 400,
          color: isActive ? C.accentText : C.filterBtnText,
          fontFamily: FF, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
        }}
      >
        {selected
          ? (selected.fileName || selected.name).replace(/\.pdf$/i, "").slice(0, 16)
          : "Resume"
        }
        {isActive && (
          <span style={{ background: C.accentBadgeBg, borderRadius: 9999, padding: "0 6px", fontSize: 10, fontWeight: 700, lineHeight: "17px", color: C.accentText }}>✓</span>
        )}
        <ChevronDown size={10} style={{ opacity: 0.6 }} />
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50,
            background: C.popupBg, border: `1px solid ${C.popupBorder}`, borderRadius: 10,
            overflow: "hidden", minWidth: 210,
            boxShadow: C.isDark ? "0 4px 6px -1px rgba(0,0,0,0.6), 0 16px 40px rgba(0,0,0,0.7)" : "0 4px 6px -1px rgba(0,0,0,0.08), 0 16px 40px rgba(0,0,0,0.12)",
          }}>
            {isActive && (
              <button
                onClick={() => { onSelect(null); setOpen(false) }}
                style={{ width: "100%", padding: "8px 12px", background: "none", border: "none", borderBottom: `1px solid ${C.popupDivider}`, cursor: "pointer", textAlign: "left", fontSize: 11, fontWeight: 600, color: C.textDim, fontFamily: FF, display: "flex", alignItems: "center", gap: 6, transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = C.bgMuted}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                Clear resume filter
              </button>
            )}
            {resumes.map(resume => {
              const isSel = resume.id === selectedResumeId
              const displayName = (resume.fileName || resume.name).replace(/\.pdf$/i, "")
              const level = resume.filters?.level
              return (
                <button
                  key={resume.id}
                  onClick={() => { onSelect(resume); setOpen(false) }}
                  style={{ width: "100%", padding: "9px 12px", background: isSel ? C.selectedItemBg : "none", border: "none", borderBottom: `1px solid ${C.popupDivider}`, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, transition: "background 0.1s" }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = C.bgMuted }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = "none" }}
                >
                  <span style={{ fontSize: 12, fontWeight: isSel ? 600 : 400, color: isSel ? C.accentText : C.textMuted, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {displayName}
                  </span>
                  {level && (
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.07em", color: C.textDim, border: `1px solid ${C.borderColor}`, borderRadius: 4, padding: "1px 6px", flexShrink: 0, fontFamily: FF }}>
                      {level}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════
export default function Dashboard() {
  const { userData } = useUser()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("theme") === "dark" } catch { return false }
  })
  const C = isDark ? CDark : CLight
  const toggleTheme = () => setIsDark(d => {
    const next = !d
    try { localStorage.setItem("theme", next ? "dark" : "light") } catch {}
    return next
  })

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener("resize", h)
    return () => window.removeEventListener("resize", h)
  }, [])

  const [activeTab, setActiveTab] = useState(() => {
    const tab = location.state?.tab
    if (tab) {
      try { window.history.replaceState({}, "", location.pathname) } catch {}
      return tab
    }
    return dashboardTabFromPath(location.pathname)
  })
  const [page, setPage]                       = useState(1)
  const [hasMore, setHasMore]                 = useState(false)
  const [showAddResume, setShowAddResume]      = useState(false)
  const [drawerOpen, setDrawerOpen]           = useState(false)
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [roleDrawerOpen, setRoleDrawerOpen] = useState(false)
  const [showCompanyModal, setShowCompanyModal] = useState(false)

  const [jobs, setJobs]                 = useState([])
  const [loading, setLoading]           = useState(false)
  const [error, setError]               = useState(null)
  const [totalResults, setTotalResults] = useState(0)

  const [savedJobIds, setSavedJobIds]         = useState(new Set())
  const [, setHeartLoadingIds] = useState(new Set())

  const [selectedJobId, setSelectedJobId] = useState(null)

  const [selectedTaxonomies, setSelectedTaxonomies] = useState([])
  const [selectedCompanies, setSelectedCompanies]   = useState([])
  const [selectedLocations, setSelectedLocations]   = useState([])
  const [selectedLevels, setSelectedLevels]         = useState([])

  // Entry-level tab has its own independent filter state
  const [freshersTaxonomies, setFreshersTaxonomies] = useState([])
  const [freshersCompanies,  setFreshersCompanies]  = useState([])
  const [freshersLocations,  setFreshersLocations]  = useState([ANYWHERE_VALUE])
  const [freshersLevels,     setFreshersLevels]     = useState(FRESHERS_DEFAULT_LEVELS)

  const jobCacheRef = useRef(new Map())

  const [resumes, setResumes]             = useState([])
  const [resumesLoaded, setResumesLoaded] = useState(false)
  const [selectedResumeId, setSelectedResumeId] = useState(null)
  const hasResumes     = resumes.length > 0
  const isFreshersTab  = activeTab === "entry-level"
  const isConsoleTab   = activeTab === "console"
  const isApplicationsTab = activeTab === "applications"
  const isWildTab      = activeTab === "wild"
  const isInboxTab     = activeTab === "inbox"
  const isProfileTab   = activeTab === "profile"
  const userId         = user?.uid
  const isEmptyState   = isConsoleTab && resumesLoaded && !hasResumes

  useEffect(() => {
    const tab = location.state?.tab || dashboardTabFromPath(location.pathname)
    setActiveTab(tab)
    if (location.state?.tab) {
      navigate(DASHBOARD_TAB_ROUTES[tab] || DASHBOARD_TAB_ROUTES.console, { replace: true })
    }
  }, [location.pathname, location.state?.tab, navigate])

  // Derive selected job from id
  const selectedJob = useMemo(() => {
    if (!selectedJobId) return null
    return jobCacheRef.current.get(selectedJobId) || null
  }, [selectedJobId])

  // Watch resumes subcollection
  useEffect(() => {
    if (!userId) return
    const unsub = onSnapshot(collection(db, "users", userId, "resumes"), snap => {
      const docs = snap.docs.map(d => ({ name: d.id, ...d.data(), id: d.id }))
      docs.sort((a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0))
      setResumes(docs)
      setResumesLoaded(true)
    })
    return () => unsub()
  }, [userId])

  // Auto-select first job whenever list changes
  useEffect(() => {
    if (jobs.length > 0 && !selectedJobId) {
      const first = jobs[0]
      const id = first.id || first.job_id
      if (id) setSelectedJobId(id)
    }
    if (jobs.length === 0) setSelectedJobId(null)
  }, [jobs])

  useEffect(() => {
    if (!userId) return
    const unsub = onSnapshot(
      collection(db, "users", userId, "track-jobs"),
      snap => {
        const ids = new Set()
        snap.forEach(d => ids.add(d.id))
        setSavedJobIds(ids)
      },
      err => console.error("Failed to watch saved jobs:", err)
    )
    return () => unsub()
  }, [userId])

  const applyAnimRef = useRef(null)
  const [pendingApplications, setPendingApplications] = useState(0)
  const [pulseApplications, setPulseApplications] = useState(0)

  // Daily auto-apply counter — mirrors mobile logic: today's right-swipes from
  // users/{uid}/swipes, limit from userData.swipeCountADay with Remote Config
  // fallback (swipe_count_a_day, default 10).
  const [swipesUsedToday, setSwipesUsedToday] = useState(0)
  const [rcSwipeLimit, setRcSwipeLimit] = useState(() => rcSwipeCountADay())
  useEffect(() => {
    let active = true
    ensureRemoteConfig().then(() => { if (active) setRcSwipeLimit(rcSwipeCountADay()) })
    return () => { active = false }
  }, [])
  const userSwipeLimit = Number(userData?.userData?.swipeCountADay ?? userData?.swipeCountADay)
  const swipeLimit = Number.isFinite(userSwipeLimit) && userSwipeLimit > 0 ? userSwipeLimit : rcSwipeLimit
  const autoAppliesLeft = Math.max(0, swipeLimit - swipesUsedToday)
  const showAutoApplyToast = () => {
    toast(`You're granted ${swipeLimit} free job applications per day`)
  }
  useEffect(() => {
    if (!userId) { setSwipesUsedToday(0); return }
    const now = new Date()
    const midnight = Timestamp.fromDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()))
    getDocs(query(
      collection(db, "users", userId, "swipes"),
      where("swipedAt", ">=", midnight),
    ))
      .then(snap => {
        let count = 0
        snap.forEach(d => { if ((d.data() || {}).direction === "right") count += 1 })
        setSwipesUsedToday(count)
      })
      .catch(() => {})
  }, [userId, pendingApplications])

  // Agent Apply right-panel takeover state. The whole flow pivots on the
  // real backend application doc:
  //   1. POST APPLICATIONS_API → doc created with status "pending_optimization"
  //   2. Panel subscribes to that doc, shows live PDF + optimization data
  //      once status flips to "pending_resume_review"
  //   3. User picks optimised/default → POST /apply/start → session begins
  //      and the choreographed overlay plays.
  // Multiple Agent Apply clicks stack into agentFlows; the panel shows one
  // "focused" flow at a time and offers a switcher for the rest.
  const [agentFlows, setAgentFlows] = useState([])
  // Shape: [{ jobKey, job, appId, defaultResumeUrl, originalResumeId, startedAt }, ...]
  const [agentFocusedKey, setAgentFocusedKey] = useState(null)

  // Local-only (not persisted) memory of job keys the user has already
  // kicked an Agent Apply flow for in this session. Prevents duplicate
  // clicks on the same card. Cleared on page refresh.
  const [startedJobKeys, setStartedJobKeys] = useState(() => new Set())
  // The one job that was just clicked — gets a 1-second spinner before the
  // card settles into its disabled "Started" state.
  const [startingJobKey, setStartingJobKey] = useState(null)

  // Promote the next flow in the list when the current focus is removed.
  const pickNextFocus = (removedKey, flows) => {
    const remaining = flows.filter(f => f.jobKey !== removedKey)
    return remaining[0]?.jobKey ?? null
  }

  const handleApply = async (job) => {
    const jobKey = String(job.id || job.job_id || job.job_url || job.jobUrl || "")
    // Guard against duplicate clicks on the same card.
    if (jobKey && startedJobKeys.has(jobKey)) return
    if (jobKey && startingJobKey === jobKey) return

    if (!userId) {
      toast.error("Please sign in before applying")
      return
    }
    const activeResume = resumes.find(r => r.id === selectedResumeId) || resumes[0]
    if (!activeResume) {
      toast.error("Please upload your resume before applying")
      return
    }
    const jobUrl = job.job_url || job.jobUrl
    if (!jobUrl) {
      toast.error("This job has no application URL")
      return
    }
    const jobTitle = job.role || job.title || "Untitled role"
    const company = job.company || "Company"

    // Show the 1-second "Starting…" spinner on the card, then flip to the
    // disabled "Started" state. The flip happens via `startedJobKeys` which
    // is marked immediately so a second click can't sneak through during
    // the spinner window.
    if (jobKey) {
      setStartingJobKey(jobKey)
      setStartedJobKeys(prev => {
        const next = new Set(prev)
        next.add(jobKey)
        return next
      })
      setTimeout(() => {
        setStartingJobKey(curr => (curr === jobKey ? null : curr))
      }, 1000)
    }

    // Close the slide-in description drawer so the right panel is visible.
    setDescOpen(false)
    if (activeTab !== "console") handleTabChange("console")

    // Prepend the new flow and auto-focus it.
    const newFlow = {
      jobKey,
      job,
      appId: null,
      defaultResumeUrl: activeResume.publicUrl || "",
      originalResumeId: activeResume.id || "",
      startedAt: Date.now(),
    }
    setAgentFlows(prev => [newFlow, ...prev.filter(f => f.jobKey !== jobKey)])
    setAgentFocusedKey(jobKey)

    try {
      const res = await fetch(APPLICATIONS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          jobUrl,
          jobTitle,
          company,
          logoUrl: job.company_logo || job.logoUrl || "",
          description: job.description || "",
          fcmToken: "",
        }),
      })
      if (res.status !== 201) throw new Error(`Application API error ${res.status}`)
      const data = await res.json().catch(() => ({}))
      let appId = data?.id || data?.applicationId || data?.application_id || ""

      // Fallback — the API doesn't always echo the id, so look up the newest
      // app doc matching this jobUrl.
      if (!appId) {
        try {
          const q = query(
            collection(db, "users", userId, "applications"),
            where("jobUrl", "==", jobUrl),
            orderBy("appliedAt", "desc"),
            limit(1),
          )
          const snap = await getDocs(q)
          if (!snap.empty) appId = snap.docs[0].id
        } catch (lookupErr) {
          console.error("Agent Apply: failed to resolve appId", lookupErr)
        }
      }

      setAgentFlows(prev => prev.map(f => f.jobKey === jobKey ? { ...f, appId } : f))
    } catch (err) {
      console.error("Failed to create application:", err)
      toast.error("Something went wrong starting Agent Apply")
      setAgentFlows(prev => {
        const next = prev.filter(f => f.jobKey !== jobKey)
        setAgentFocusedKey(curr => (curr === jobKey ? pickNextFocus(jobKey, prev) : curr))
        return next
      })
      // Allow a retry — clear the "started" guard for this job.
      if (jobKey) {
        setStartedJobKeys(prev => {
          const next = new Set(prev)
          next.delete(jobKey)
          return next
        })
      }
    }
  }

  const executeApply = async (jobKey, variant = "optimised", appData = null) => {
    const flow = agentFlows.find(f => f.jobKey === jobKey)
    if (!flow) return
    const { job, appId, originalResumeId } = flow
    const jobUrl = job.job_url || job.jobUrl
    const optimisedResumeId = appData?.optimisedResumeId || ""
    const chosenResumeId = variant === "optimised" && optimisedResumeId ? optimisedResumeId : originalResumeId

    // Same guard ApplicationsTab's startSession uses — without a jobUrl /
    // userId / appId the agent API will 400 and the panel would get stuck
    // waiting for events that never come.
    if (!jobUrl || !userId || !appId) {
      toast.error("Can't start Agent Apply yet — missing details")
      throw new Error("Missing jobUrl / userId / appId for apply/start")
    }

    try {
      const res = await fetch(`${APPLY_AGENT_API}/apply/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_url: jobUrl,
          email: user?.email || "",
          password: DEFAULT_WORKDAY_PASSWORD,
          user_id: userId,
          resume_id: chosenResumeId || "",
          application_id: appId,
        }),
      })
      if (!res.ok) throw new Error(`Failed to start session (${res.status})`)
      if (variant === "optimised") {
        setDoc(
          doc(db, "users", userId, "applications", appId),
          { useOptimizedResume: true },
          { merge: true },
        ).catch(() => {})
      }
      toast.success("Agent will apply in the background")
    } catch (err) {
      console.error("Failed to start apply session:", err)
      toast.error("Failed to start application")
      throw err
    }
  }

  const cancelApply = async (jobKey) => {
    const flow = agentFlows.find(f => f.jobKey === jobKey)
    const appId = flow?.appId
    setAgentFlows(prev => {
      const next = prev.filter(f => f.jobKey !== jobKey)
      setAgentFocusedKey(curr => (curr === jobKey ? pickNextFocus(jobKey, prev) : curr))
      return next
    })
    // Clean up the pending app doc the user never confirmed.
    if (appId && userId) {
      try {
        await deleteDoc(doc(db, "users", userId, "applications", appId))
      } catch (err) {
        console.error("Failed to cancel pending application:", err)
      }
    }
  }

  const handleParticleArrive = () => {
    setPendingApplications(n => n + 1)
    setPulseApplications(n => n + 1)
  }

  const hasInitedFilters     = useRef(false)
  const lastSavedFilterKey   = useRef(null)
  const saveTimerRef         = useRef(null)
  const [filtersReady, setFiltersReady]           = useState(false)
  const [filtersKey, setConsoleFiltersKey] = useState(0)

  // Init: load saved console filters from Firestore, auto-select resume
  useEffect(() => {
    if (!userData || !userId || !resumesLoaded) return
    if (userData.onboardingComplete !== true) { navigate("/register", { state: { step: 3 } }); return }
    if (hasInitedFilters.current) return
    hasInitedFilters.current = true

    if (resumes.length === 0) { setFiltersReady(true); return }

    const applyResume = (r, savedTaxonomies, savedLevel, savedLocations) => {
      setSelectedResumeId(r.id)
      // Prefer user's saved (tweaked) taxonomies over the resume's raw list
      if (savedTaxonomies && savedTaxonomies.length > 0) {
        setSelectedTaxonomies(savedTaxonomies)
      } else {
        const items = (r.filters?.taxonomies || []).map(id => TAXONOMY_MAP.get(id)).filter(Boolean)
        setSelectedTaxonomies(items)
      }
      // Level: user doc first, then resume
      const level = savedLevel || r.filters?.level
      setSelectedLevels(level ? [level] : [])
      // Locations: user doc first, then resume (resume now persists them too)
      const locations = savedLocations ?? (r.filters?.locations?.length > 0 ? r.filters.locations : null)
      if (locations) setSelectedLocations(locations)
    }

    getDoc(doc(db, "users", userId))
      .then(snap => {
        const saved = snap.exists() ? snap.data()?.filters : null
        const savedResume = saved?.resumeId ? resumes.find(r => r.id === saved.resumeId) : null
        const target = savedResume || resumes[0]
        applyResume(
          target,
          Array.isArray(saved?.taxonomies) ? saved.taxonomies : null,
          saved?.level || null,
          Array.isArray(saved?.locations) ? saved.locations : null,
        )
        setFiltersReady(true)
      })
      .catch(() => {
        const r = resumes[0]
        const items = (r.filters?.taxonomies || []).map(id => TAXONOMY_MAP.get(id)).filter(Boolean)
        setSelectedResumeId(r.id)
        setSelectedTaxonomies(items)
        setSelectedLevels(r.filters?.level ? [r.filters.level] : [])
        setFiltersReady(true)
      })
  }, [userData, userId, resumesLoaded])


  const handleHeartToggle = async (job) => {
    if (!userId) return
    const jobId = job.id || job.job_id; if (!jobId) return
    setHeartLoadingIds(prev => new Set(prev).add(jobId))
    const wasSaved = savedJobIds.has(jobId)
    setSavedJobIds(prev => { const next = new Set(prev); wasSaved ? next.delete(jobId) : next.add(jobId); return next })
    try {
      const ref = doc(db, "users", userId, "track-jobs", jobId)
      if (wasSaved) { await deleteDoc(ref) } else { await setDoc(ref, { ...job, stage: "WISHLIST", saved_at: new Date().toISOString() }) }
    } catch (err) {
      console.error("Failed to toggle saved job:", err)
      setSavedJobIds(prev => { const next = new Set(prev); wasSaved ? next.add(jobId) : next.delete(jobId); return next })
    } finally {
      setHeartLoadingIds(prev => { const next = new Set(prev); next.delete(jobId); return next })
    }
  }

  const handleJobCardClick = (job) => {
    const id = job.id || job.job_id
    if (!id) return
    jobCacheRef.current.set(id, job)
    setSelectedJobId(id)
  }

  const handleResumeSelect = (resume) => {
    if (!resume) {
      setSelectedResumeId(null)
      setSelectedTaxonomies([])
      setSelectedLevels([])
      return
    }
    setSelectedResumeId(resume.id)
    const taxonomyItems = (resume.filters?.taxonomies || [])
      .map(id => TAXONOMY_MAP.get(id))
      .filter(Boolean)
    setSelectedTaxonomies(taxonomyItems)
    const level = resume.filters?.level
    setSelectedLevels(level ? [level] : [])
    if (resume.filters?.locations?.length > 0) setSelectedLocations(resume.filters.locations)
  }

  function buildConsolePayload() {
    return {
      userId,
      jobTaxonomyList: selectedTaxonomies,
      seniority_level: selectedLevels.join(","),
      location: selectedLocations.length > 0 ? selectedLocations.join(",") : FRESHERS_DEFAULT_LOCATION,
      page,
    }
  }
  function buildFreshersPayload() {
    return {
      userId,
      jobTaxonomyList: freshersTaxonomies.length > 0 ? freshersTaxonomies : SWE_DEFAULT_TAXONOMY,
      location: freshersLocations.length > 0 ? freshersLocations.join(",") : FRESHERS_DEFAULT_LOCATION,
      seniority_level: freshersLevels.join(","),
      entryL: true,
      page,
    }
  }

  useEffect(() => {
    if (!isConsoleTab || !userId || !filtersReady) return
    setLoading(true); setError(null); setJobs([]); setHasMore(false); setSelectedJobId(null)
    fetch(API_ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(buildConsolePayload()) })
      .then(res => { if (!res.ok) throw new Error(`API error ${res.status}`); return res.json() })
      .then(data => {
        const l = data.listings || []
        l.forEach(j => jobCacheRef.current.set(j.id || j.job_id, j))
        setJobs(l)
        setTotalResults(data.total_results || 0)
        setHasMore(data.has_more !== undefined ? Boolean(data.has_more) : l.length === 20)
      })
      .catch(err => { console.error("Job fetch failed:", err); setError("Failed to load jobs.") })
      .finally(() => setLoading(false))
  }, [isConsoleTab, userId, filtersReady, filtersKey, selectedTaxonomies, selectedLevels, selectedLocations, selectedCompanies, page])

  useEffect(() => {
    if (!isFreshersTab || !userId) return
    setLoading(true); setError(null); setJobs([]); setHasMore(false); setSelectedJobId(null)
    fetch(API_ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(buildFreshersPayload()) })
      .then(res => { if (!res.ok) throw new Error(`API error ${res.status}`); return res.json() })
      .then(data => {
        const l = data.listings || []
        l.forEach(j => jobCacheRef.current.set(j.id || j.job_id, j))
        setJobs(l)
        setTotalResults(data.total_results || 0)
        setHasMore(data.has_more !== undefined ? Boolean(data.has_more) : l.length === 20)
      })
      .catch(err => { console.error("Entry level fetch failed:", err); setError("Failed to load jobs.") })
      .finally(() => setLoading(false))
  }, [isFreshersTab, userId, page, freshersTaxonomies, freshersCompanies, freshersLocations, freshersLevels])

  const handlePageChange = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }) }
  const handleTabChange  = (tab) => {
    setActiveTab(tab)
    setPage(1)
    if (tab === "applications") setPendingApplications(0)
    navigate(DASHBOARD_TAB_ROUTES[tab] || DASHBOARD_TAB_ROUTES.console)
  }
  const handleSignOut = async () => { await signOut(); navigate("/login") }

  // Derive active filter values based on current tab so the UI is always tab-aware
  const activeSelectedTaxonomies = isFreshersTab ? freshersTaxonomies : selectedTaxonomies
  const activeSelectedCompanies  = isFreshersTab ? freshersCompanies  : selectedCompanies
  const activeSelectedLocations  = isFreshersTab ? freshersLocations  : selectedLocations
  const activeSelectedLevels     = isFreshersTab ? freshersLevels     : selectedLevels

  const filters = {
    selectedTaxonomies: activeSelectedTaxonomies,
    selectedCompanies:  activeSelectedCompanies,
    selectedLocations:  activeSelectedLocations,
    selectedLevels:     activeSelectedLevels,
  }
  const filterSetters = {
    onTaxonomyToggle: ({ taxonomyId, title }) => {
      const setter = isFreshersTab ? setFreshersTaxonomies : setSelectedTaxonomies
      setter(prev =>
        prev.some(t => t.taxonomyId === taxonomyId)
          ? prev.filter(t => t.taxonomyId !== taxonomyId)
          : [...prev, { taxonomyId, title }]
      )
    },
    onTaxonomyClear: () => isFreshersTab ? setFreshersTaxonomies([]) : setSelectedTaxonomies([]),
    setSelectedCompanies: (val) => {
      const curr = isFreshersTab ? freshersCompanies : selectedCompanies
      const setter = isFreshersTab ? setFreshersCompanies : setSelectedCompanies
      setter(typeof val === "function" ? val(curr) : val)
    },
    setSelectedLocations: (val) => {
      const curr = isFreshersTab ? freshersLocations : selectedLocations
      const setter = isFreshersTab ? setFreshersLocations : setSelectedLocations
      setter(typeof val === "function" ? val(curr) : val)
    },
    setSelectedLevels: (val) => {
      const curr = isFreshersTab ? freshersLevels : selectedLevels
      const setter = isFreshersTab ? setFreshersLevels : setSelectedLevels
      setter(typeof val === "function" ? val(curr) : val)
    },
    onReset: () => {
      if (isFreshersTab) {
        setFreshersTaxonomies([])
        setFreshersLocations([LOCATIONS[0].value])
        setFreshersLevels(FRESHERS_DEFAULT_LEVELS)
        setFreshersCompanies([])
      } else {
        const target = resumes.find(r => r.id === selectedResumeId) || resumes[0]
        if (target) {
          setSelectedResumeId(target.id)
          const items = (target.filters?.taxonomies || []).map(id => TAXONOMY_MAP.get(id)).filter(Boolean)
          setSelectedTaxonomies(items)
          setSelectedLevels(target.filters?.level ? [target.filters.level] : [])
        } else {
          setSelectedTaxonomies([]); setSelectedResumeId(null); setSelectedLevels([])
        }
        setSelectedLocations([]); setSelectedCompanies([])
      }
    },
  }

  const consoleFilterKey = `${selectedResumeId || ""}|${selectedTaxonomies.map(t => t.taxonomyId).join(",")}|${selectedLocations.join(",")}|${selectedLevels.join(",")}|${selectedCompanies.join(",")}`
  const freshersFilterKey = `${freshersTaxonomies.map(t => t.taxonomyId).join(",")}|${freshersLocations.join(",")}|${freshersLevels.join(",")}|${freshersCompanies.join(",")}`
  useEffect(() => { setPage(1) }, [consoleFilterKey, freshersFilterKey])

  // Debounce-save console filters to Firestore whenever they change
  useEffect(() => {
    if (!isConsoleTab || !filtersReady || !userId) return
    if (consoleFilterKey === lastSavedFilterKey.current) return

    clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(async () => {
      try {
        const saves = [
          // User doc — tracks current working filters (full taxonomy objects + locations)
          setDoc(
            doc(db, "users", userId),
            { filters: { resumeId: selectedResumeId || null, taxonomies: selectedTaxonomies, locations: selectedLocations, level: selectedLevels[0] || null } },
            { merge: true }
          ),
        ]
        // Resume doc — keeps the resume's filter snapshot in sync (taxonomy IDs format)
        if (selectedResumeId) {
          saves.push(setDoc(
            doc(db, "users", userId, "resumes", selectedResumeId),
            { filters: { taxonomies: selectedTaxonomies.map(t => t.taxonomyId), level: selectedLevels[0] || null, locations: selectedLocations } },
            { merge: true }
          ))
        }
        await Promise.all(saves)
        lastSavedFilterKey.current = consoleFilterKey
      } catch (err) {
        console.error("Failed to save console filters:", err)
      }
    }, 800)

    return () => clearTimeout(saveTimerRef.current)
  }, [consoleFilterKey, isConsoleTab, filtersReady, userId])

  const activeFilterCount = activeSelectedTaxonomies.length + activeSelectedCompanies.length + activeSelectedLocations.length + activeSelectedLevels.length

  const activeNavId = isFreshersTab ? "entry-level"
    : isApplicationsTab ? "applications"
    : isWildTab ? "wild"
    : isInboxTab ? "inbox"
    : isProfileTab ? "profile"
    : "console"

  const [descOpen, setDescOpen] = useState(false)
  useEffect(() => { setDescOpen(false) }, [activeTab])
  const handleJobCardClickOpen = (job) => {
    handleJobCardClick(job)
    setDescOpen(true)
  }
  const handlePassJob = (job) => {
    const id = job?.id || job?.job_id
    if (!id) return
    setJobs(prev => prev.filter(j => (j.id || j.job_id) !== id))
  }

  const useMobileHomeOverlay = isMobile && isConsoleTab && !isEmptyState
  const useMobileProfileOverlay = isMobile && isProfileTab
  const useMobileApplicationsOverlay = isMobile && isApplicationsTab
  const useMobileInboxOverlay = isMobile && isInboxTab
  const useMobileWildOverlay = isMobile && isWildTab

  return (
    <div style={{
      height: "100vh",
      background: C.bgPage,
      color: C.text,
      fontFamily: FF,
      display: "flex",
      overflow: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes spin       { to { transform: rotate(360deg); } }
        @keyframes fadeUp     { from { opacity: 0; transform: translateY(8px); }  to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDownMenu { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.borderColor}; border-radius: 2px; }
        .dash-sidebar  { display: flex; flex-shrink: 0; }
        .dash-content  { flex: 1; min-width: 0; display: flex; flex-direction: column; overflow: hidden; }
        .dash-mobile-top {
          display: none;
          padding: 10px 14px;
          background: ${C.topbarBg};
          border-bottom: 1px solid ${C.borderColor};
          flex-shrink: 0;
        }
        .dash-body {
          flex: 1;
          display: flex;
          overflow: hidden;
          min-height: 0;
        }
        .dash-job-scroll {
          flex: 1;
          overflow-y: auto;
          padding: 18px 24px 48px;
          background: ${C.mainBg};
          min-height: 0;
          scrollbar-width: none;         /* Firefox */
          -ms-overflow-style: none;      /* legacy Edge/IE */
        }
        .dash-job-scroll::-webkit-scrollbar { display: none; }
        .job-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .dash-right-panel {
          flex-shrink: 0;
          width: 20vw;
          max-width: 20vw;
          min-width: 260px;
          background: #ffffff;
          overflow-y: auto;
        }
        .dash-job-col {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          overflow: visible;
        }
        .dash-filter-bar-wrap {
          padding: 18px 24px 14px;
          background: ${C.topbarBg};
          border-bottom: 1px solid ${C.borderColor};
          flex-shrink: 0;
        }
        .dash-filter-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        }
        .dash-filter-head-left { min-width: 0; }
        .dash-filter-head-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          flex-shrink: 0;
        }
        .dash-auto-apply-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 40px;
          padding: 0 14px;
          background: #f3f4f6;
          border: none;
          border-radius: 9999px;
          font-family: ${FF};
          color: ${C.text};
          flex-shrink: 0;
          cursor: pointer;
          transition: background 0.15s ease;
        }
        .dash-auto-apply-pill:hover { background: #e8eaed; }
        .dash-auto-apply-pill:focus-visible { outline: 2px solid #c7d2fe; outline-offset: 2px; }
        .dash-auto-apply-num {
          font-size: 13px;
          font-weight: 800;
          color: ${C.text};
          font-variant-numeric: tabular-nums;
        }
        .dash-auto-apply-lbl {
          font-size: 12px;
          font-weight: 600;
          color: ${C.textDim};
          white-space: nowrap;
        }
        /* Single bordered widget — thumb slides inside */
        .dash-xp-toggle {
          position: relative;
          display: inline-grid;
          grid-template-columns: 1fr 1fr;
          align-items: center;
          height: 40px;
          padding: 4px;
          background: #ffffff;
          border: 1px solid #e4e4e7;
          border-radius: 9999px;
          box-shadow: inset 0 1px 2px rgba(0,0,0,0.03);
          flex-shrink: 0;
          isolation: isolate;
        }
        .dash-xp-thumb {
          position: absolute;
          top: 4px; bottom: 4px; left: 4px;
          width: calc(50% - 4px);
          background: #e4e4e7;
          border-radius: 9999px;
          z-index: 0;
          box-shadow: 0 1px 2px rgba(0,0,0,0.06);
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          will-change: transform;
        }
        .dash-xp-toggle[data-active="experienced"] .dash-xp-thumb {
          transform: translateX(100%);
        }
        .dash-xp-opt {
          position: relative;
          z-index: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 30px;
          padding: 0 18px;
          background: transparent;
          border: none;
          border-radius: 9999px;
          font-family: ${FF};
          font-size: 12.5px;
          font-weight: 600;
          color: #71717a;
          cursor: pointer;
          transition: color 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          white-space: nowrap;
        }
        .dash-xp-opt:hover { color: #18181b; }
        .dash-xp-opt.on { color: #18181b; }
        .dash-xp-opt.on:hover { color: #18181b; }
        .dash-filter-count {
          margin: 0;
          font-size: 22px;
          font-weight: 700;
          letter-spacing: -0.025em;
          color: ${C.text};
          font-family: ${FF};
          line-height: 1.15;
        }
        .dash-filter-count-num {
          color: ${C.text};
          font-weight: 800;
        }
        .dash-filter-sub {
          margin: 2px 0 0;
          font-size: 12px;
          color: ${C.textDim};
          font-family: ${FF};
          font-weight: 500;
        }
        @media (max-width: 980px) {
          .dash-filter-head { flex-direction: column; align-items: stretch; gap: 14px; }
          .dash-filter-head-right { justify-content: flex-start; }
        }
        @media (max-width: 768px) {
          .dash-sidebar { display: none !important; }
          .job-grid { gap: 10px; }
          .dash-job-scroll { padding: 12px 14px 40px; }
          .dash-mobile-top { display: flex !important; }
          .mobile-hamburger { display: flex !important; }
          .desktop-filters { display: none !important; }
          .dash-right-panel { display: none !important; }
          .dash-filter-bar-wrap { padding: 4px 0 12px; }
          .dash-filter-count { font-size: 18px; }
        }
      `}</style>

      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeTab={activeNavId}
        onTabClick={handleTabChange}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        C={C}
        user={user}
        userData={userData}
        onSignOut={handleSignOut}
        logoImg={logoImg}
        logoImgDark={logoImgDark}
      />
      <MobileFilterPanel
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        filters={filters}
        filterSetters={filterSetters}
        C={C}
        isFreshers={isFreshersTab}
      />
      <RoleSelectionModal
        variant="drawer"
        open={roleDrawerOpen}
        onClose={() => setRoleDrawerOpen(false)}
        selected={activeSelectedTaxonomies}
        onToggle={filterSetters.onTaxonomyToggle}
        onClear={filterSetters.onTaxonomyClear}
        selectedCompanies={activeSelectedCompanies}
        onCompanyToggle={(name) => filterSetters.setSelectedCompanies(prev =>
          prev.includes(name) ? prev.filter(item => item !== name) : [...prev, name]
        )}
        onCompanyClear={() => filterSetters.setSelectedCompanies([])}
      />

      {/* ── SIDEBAR ── */}
      <div className="dash-sidebar">
        <Sidebar
          activeTab={activeNavId}
          onTabChange={handleTabChange}
          isDark={isDark}
          onToggleTheme={toggleTheme}
          C={C}
          user={user}
          userData={userData}
          onSignOut={handleSignOut}
          logoImg={logoImg}
          logoImgDark={logoImgDark}
          applicationsBadge={pendingApplications}
          pulseApplications={pulseApplications}
        />
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="dash-content">
        {/* Mobile top bar — hamburger only (hidden when a mobile-tab overlay is up) */}
        {!useMobileHomeOverlay && !useMobileProfileOverlay && !useMobileApplicationsOverlay && !useMobileInboxOverlay && (
          <div className="dash-mobile-top">
            <button
              className="mobile-hamburger"
              onClick={() => setDrawerOpen(true)}
              style={{ width: 34, height: 34, borderRadius: 4, flexShrink: 0, background: "#fff", border: `1px solid ${C.borderColor}`, display: "none", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.textMuted }}
            >
              <Menu size={17} strokeWidth={1.8} />
            </button>
          </div>
        )}

        {/* Full-width filter header (spans both job column and right panel) */}
        {!isEmptyState && !isApplicationsTab && !isWildTab && !isInboxTab && !isProfileTab && (
          <div className="desktop-filters dash-filter-bar-wrap">
            <div className="dash-filter-head">
              <div className="dash-filter-head-left">
                <h2 className="dash-filter-count">
                  <span className="dash-filter-count-num">{(totalResults || 0).toLocaleString()}</span>
                  {" "}Relevant Jobs
                </h2>
                <p className="dash-filter-sub">found across internet</p>
              </div>
              <div className="dash-filter-head-right">
                <button
                  type="button"
                  className="dash-auto-apply-pill"
                  aria-label={`${autoAppliesLeft} auto applies left today`}
                  onClick={showAutoApplyToast}
                >
                  <span className="dash-auto-apply-num">{autoAppliesLeft}</span>
                  <span className="dash-auto-apply-lbl">Auto Apply left</span>
                </button>
                <div
                  className="dash-xp-toggle"
                  role="tablist"
                  aria-label="Experience"
                  data-active={isFreshersTab ? "fresher" : "experienced"}
                >
                  <span className="dash-xp-thumb" aria-hidden="true" />
                  <button
                    role="tab"
                    aria-selected={isFreshersTab}
                    onClick={() => !isFreshersTab && handleTabChange("entry-level")}
                    className={`dash-xp-opt${isFreshersTab ? " on" : ""}`}
                  >
                    Fresher
                  </button>
                  <button
                    role="tab"
                    aria-selected={isConsoleTab}
                    onClick={() => !isConsoleTab && handleTabChange("console")}
                    className={`dash-xp-opt${isConsoleTab ? " on" : ""}`}
                  >
                    Experienced
                  </button>
                </div>
                <FilterBar
                  C={C}
                  selectedTaxonomies={activeSelectedTaxonomies}
                  onTaxonomyToggle={filterSetters.onTaxonomyToggle}
                  onTaxonomyClear={filterSetters.onTaxonomyClear}
                  selectedCompanies={activeSelectedCompanies}
                  onCompanyToggle={(name) =>
                    filterSetters.setSelectedCompanies(prev =>
                      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
                    )
                  }
                  onCompanyClear={() => filterSetters.setSelectedCompanies([])}
                  onSearch={() => setConsoleFiltersKey(k => k + 1)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="dash-body">
          <div className="dash-job-col" style={{ overflow: "hidden" }}>
            {/* Scrollable grid */}
            <div
              className="dash-job-scroll"
              key={activeTab}
              style={{
                animation: "fadeUp 0.2s cubic-bezier(0.22,1,0.36,1)",
                ...(isInboxTab ? {
                  padding: 0,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                } : null),
              }}>
              {isWildTab ? (
                <WildTab userId={userId} userName={userData?.name || user?.displayName || ""} />
              ) : isInboxTab ? (
                <InboxTab userId={userId} />
              ) : isProfileTab ? (
                <ProfileTab userId={userId} userData={userData} />
              ) : isApplicationsTab ? (
                <ApplicationsTab userId={userId} user={user} resumeId={selectedResumeId} />
              ) : isConsoleTab && !resumesLoaded ? (
                <Spinner C={C} />
              ) : isConsoleTab && !hasResumes ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, minHeight: "70vh" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24, textAlign: "center", maxWidth: 340 }}>
                    <div style={{ width: 72, height: 72, borderRadius: 10, border: `1.5px dashed ${C.borderMid}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <User size={28} color={C.textDim} strokeWidth={1.5} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.text, fontFamily: FF, letterSpacing: "-0.02em" }}>We don't know you yet</p>
                      <p style={{ margin: 0, fontSize: 13, color: C.textDim, fontFamily: FF, lineHeight: 1.7 }}>Upload your resume so we can match you with the right roles.</p>
                    </div>
                    <button
                      onClick={() => setShowAddResume(true)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 40, padding: "0 22px", background: C.checkFillBg, border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: FF, color: C.checkFillIcon, transition: "opacity 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                      onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                    >
                      <UploadCloud size={15} /> Upload resume
                    </button>
                  </div>
                </div>
              ) : loading ? (
                <div className="job-grid">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <DesktopJobCardSkeleton key={i} />
                  ))}
                </div>
              ) : error ? (
                <ErrorState onRetry={() => window.location.reload()} C={C} />
              ) : (
                <>
                  <div className="job-grid">
                    {jobs.map((job, i) => (
                      <DesktopJobCard
                        key={job.id || job.job_id}
                        job={job}
                        onClick={handleJobCardClickOpen}
                        selected={(job.id || job.job_id) === selectedJobId}
                        savedJobIds={savedJobIds}
                        onHeartToggle={handleHeartToggle}
                        onApply={handleApply}
                        onPass={handlePassJob}
                        isApplyStarting={startingJobKey === String(job.id || job.job_id || job.job_url || job.jobUrl || "")}
                        isApplyStarted={startedJobKeys.has(String(job.id || job.job_id || job.job_url || job.jobUrl || ""))}
                      />
                    ))}
                  </div>
                  <Pagination
                    current={page}
                    total={totalResults > 0 ? Math.ceil(totalResults / 20) : 0}
                    hasMore={hasMore}
                    onChange={handlePageChange}
                    C={C}
                  />
                </>
              )}
            </div>
          </div>
          {!isApplicationsTab && !isWildTab && !isInboxTab && !isProfileTab && !isEmptyState && (
            <aside
              className="dash-right-panel"
              aria-label={agentFlows.length > 0 ? "Agent Apply flow" : "Saved filters panel"}
            >
              {agentFlows.length > 0 ? (
                <AgentApplyPanel
                  userId={userId}
                  flows={agentFlows}
                  focusedKey={agentFocusedKey || agentFlows[0]?.jobKey}
                  onFocus={setAgentFocusedKey}
                  onCancel={cancelApply}
                  onApply={executeApply}
                />
              ) : (
                <DashboardRightPanel
                  user={user}
                  userData={userData}
                  resumes={resumes}
                  selectedResumeId={selectedResumeId}
                  selectedTaxonomies={activeSelectedTaxonomies}
                  selectedLocations={activeSelectedLocations}
                  selectedLevels={activeSelectedLevels}
                  onAddFilter={() => toast("Saved filters coming soon", { icon: "✨" })}
                  onEditFilter={() => setRoleDrawerOpen(true)}
                />
              )}
            </aside>
          )}
        </div>
      </div>

      {/* ── Slide-in Job Description Drawer ── */}
      <JobDescPanel
        open={descOpen && !!selectedJob}
        job={selectedJob}
        loading={loading && !selectedJob}
        savedJobIds={savedJobIds}
        onHeartToggle={handleHeartToggle}
        onApply={handleApply}
        onPass={handlePassJob}
        onClose={() => setDescOpen(false)}
      />

      {showAddResume && (
        <AddResumeModal
          userId={userId}
          theme={isDark ? "dark" : "light"}
          onClose={() => setShowAddResume(false)}
          onUploaded={(resume) => {
            setSelectedLevels(resume?.seniority_level ? parseSeniority(resume.seniority_level) : [])
            hasInitedFilters.current = true
            setFiltersReady(true)
          }}
        />
      )}

      {showCompanyModal && (
        <CompanyPickerModal
          selected={selectedCompanies}
          onToggle={(name) => {
            const setter = isFreshersTab ? setFreshersCompanies : setSelectedCompanies
            setter(prev => {
              const next = prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
              if (!isFreshersTab) {
                if (!prev.includes(name) && prev.length === 0) setSelectedLevels([])
                if (prev.includes(name) && prev.length === 1)
                  setSelectedLevels(userData?.resume?.seniority_level ? parseSeniority(userData.resume.seniority_level) : [])
              }
              return next
            })
          }}
          onClear={() => isFreshersTab ? setFreshersCompanies([]) : setSelectedCompanies([])}
          onClose={() => setShowCompanyModal(false)}
          onDone={() => setShowCompanyModal(false)}
          C={C}
        />
      )}

      <style>{`
        .mobile-filter-fab { display: none !important; }
        @media (max-width: 768px) {
          .mobile-filter-fab {
            display: inline-flex !important;
            position: fixed; bottom: 24px; left: 50%;
            transform: translateX(-50%);
            z-index: 200;
            align-items: center; gap: 7px;
            padding: 0 20px; height: 38px;
            background: ${C.bgCard};
            color: ${C.text};
            border: 1px solid ${C.borderColor};
            border-radius: 9999px;
            font-size: 13px; font-weight: 600; font-family: ${FF};
            cursor: pointer;
            box-shadow: 0 4px 16px rgba(0,0,0,0.5);
            white-space: nowrap;
          }
        }
      `}</style>

      {!isApplicationsTab && !isWildTab && !isInboxTab && !isProfileTab && !(isMobile && selectedJob) && !useMobileHomeOverlay && (
        <button className="mobile-filter-fab" onClick={() => setFilterPanelOpen(true)}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="2" y1="4" x2="14" y2="4" />
            <line x1="4" y1="8" x2="12" y2="8" />
            <line x1="6" y1="12" x2="10" y2="12" />
          </svg>
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </button>
      )}

      {useMobileInboxOverlay && (
        <MobileInboxTab
          userId={userId}
          onTabChange={handleTabChange}
          activeTab={activeNavId}
          pendingApplications={pendingApplications}
        />
      )}

      {useMobileWildOverlay && (
        <MobileWildTab
          userId={userId}
          onTabChange={handleTabChange}
          activeTab={activeNavId}
          pendingApplications={pendingApplications}
        />
      )}

      {useMobileApplicationsOverlay && (
        <MobileApplicationsTab
          userId={userId}
          user={user}
          resumeId={selectedResumeId}
          onTabChange={handleTabChange}
          activeTab={activeNavId}
          pendingApplications={pendingApplications}
        />
      )}

      {useMobileProfileOverlay && (
        <MobileProfileTab
          userId={userId}
          userData={userData}
          onTabChange={handleTabChange}
          activeTab={activeNavId}
          pendingApplications={pendingApplications}
          onOpenSettings={() => setDrawerOpen(true)}
        />
      )}

      {useMobileHomeOverlay && (
        <MobileHomeTab
          jobs={jobs}
          loading={loading || !filtersReady || !resumesLoaded}
          error={error}
          savedJobIds={savedJobIds}
          onHeartToggle={handleHeartToggle}
          onApply={handleApply}
          onPass={handlePassJob}
          onTabChange={handleTabChange}
          pendingApplications={pendingApplications}
          onOpenFilters={() => setFilterPanelOpen(true)}
          onOpenAddResume={() => setShowAddResume(true)}
          activeTab={activeNavId}
          swipesUsed={swipesUsedToday}
          swipeLimit={swipeLimit}
        />
      )}

      <ApplyAnimation ref={applyAnimRef} onArrive={handleParticleArrive} />
    </div>
  )
}
